import React from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

export interface AdminPaginationProps {
  page: number
  limit: number
  total: number
  onPageChange: (page: number) => void
  onLimitChange?: (limit: number) => void
  limitOptions?: number[]
  className?: string
  showInfo?: boolean
  showLimitSelector?: boolean
  isLoading?: boolean
}

export default function AdminPagination({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  limitOptions = [10, 20, 50, 100],
  className = '',
  showInfo = true,
  showLimitSelector = true,
  isLoading = false,
}: AdminPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const startRecord = total === 0 ? 0 : (page - 1) * limit + 1
  const endRecord = Math.min(page * limit, total)

  // Max number of pagination buttons to show
  const maxButtons = 5
  let startPage = Math.max(1, page - Math.floor(maxButtons / 2))
  let endPage = Math.min(totalPages, startPage + maxButtons - 1)

  if (endPage - startPage + 1 < maxButtons) {
    startPage = Math.max(1, endPage - maxButtons + 1)
  }

  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-3 ${className}`}>
      {/* Left: Info Counter & Limit Selector */}
      <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-600">
        {showInfo && (
          <span>
            {total === 0 ? 'No records found' : `Showing ${startRecord}–${endRecord} of ${total} records`}
          </span>
        )}

        {showLimitSelector && onLimitChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="limit-select" className="text-gray-500 font-medium">
              Rows per page:
            </label>
            <select
              id="limit-select"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              disabled={isLoading}
              className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] disabled:opacity-50"
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Right: Page Navigation Controls */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* First Page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={page <= 1 || isLoading}
          className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="First Page"
          aria-label="First Page"
        >
          <ChevronsLeft size={16} />
        </button>

        {/* Previous Page */}
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1 || isLoading}
          className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Previous Page"
          aria-label="Previous Page"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Page Buttons */}
        {startPage > 1 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] transition-colors"
            >
              1
            </button>
            {startPage > 2 && <span className="px-1 text-gray-400">...</span>}
          </>
        )}

        {pageNumbers.map((p) => {
          const isActive = p === page
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              disabled={isLoading}
              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-[#6D3A0D] text-white border border-[#6D3A0D] shadow-sm'
                  : 'border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] hover:border-gray-400'
              } disabled:opacity-50`}
            >
              {p}
            </button>
          )
        })}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1 text-gray-400">...</span>}
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next Page */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || isLoading}
          className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Next Page"
          aria-label="Next Page"
        >
          <ChevronRight size={16} />
        </button>

        {/* Last Page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages || isLoading}
          className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-[#F6EEE7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Last Page"
          aria-label="Last Page"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  )
}
